import React, {useState, useEffect, useCallback} from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import SupportPromptGroup from '@cloudscape-design/chat-components/support-prompt-group';
import {Box, Button, Header, SpaceBetween, SplitPanel, Textarea} from '@cloudscape-design/components';
import AppLayout from '@cloudscape-design/components/app-layout';
import {useStickToBottom} from 'use-stick-to-bottom';
import ChatPanel from './chat-panel';
import ChatMessage from './chat-message';
import {
    Chat,
    ChatMessage as ApiMessage,
    StreamEvent,
    listChats,
    createChat,
    deleteChat,
    getMessages,
    sendMessageStream,
    setUserId,
} from './cassandra-api';
import {useAuth, ServiceResource} from '../hooks/use-auth';
import './cassandra.css';

interface Message {
    id: string;
    role: 'user' | 'assistant';
    text: string;
    steps?: string[];
    timestamp: number;
}

function extractText(msg: ApiMessage): string {
    return msg.parts
        .filter((p) => p.type === 'text' && p.text)
        .map((p) => p.text!)
        .join('\n');
}

export default function CassandraChat() {
    const auth = useAuth();
    const credentials = auth.authInformation!.getCurrentServiceEndpoint(ServiceResource.ExplainabilityView).credentials;
    const userAlias = auth.authInformation?.email?.split('@')[0];

    useEffect(() => {
        if (auth.authInformation?.email) setUserId(auth.authInformation.email);
    }, [auth.authInformation?.email]);

    const [chats, setChats] = useState<Chat[]>([]);
    const [chatsLoading, setChatsLoading] = useState(true);
    const [activeChatId, setActiveChatId] = useState<string | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [splitPanelOpen, setSplitPanelOpen] = useState(false);
    const [splitPanelContent, setSplitPanelContent] = useState<string>('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const {scrollRef, contentRef, isAtBottom, scrollToBottom} = useStickToBottom();

    useEffect(() => {
        if (!credentials) return;
        setChatsLoading(true);
        listChats(credentials)
            .then(setChats)
            .catch(() => setChats([]))
            .finally(() => setChatsLoading(false));
    }, [credentials]);

    useEffect(() => {
        if (!activeChatId) {
            setMessages([]);
            return;
        }
        if (sending) return;
        getMessages(activeChatId, credentials!).then((msgs) =>
            setMessages(
                msgs
                    .filter((m) => m.role === 'user' || m.role === 'assistant')
                    .filter((m) => extractText(m).trim())
                    .map((m) => ({id: m.id, role: m.role, text: extractText(m), timestamp: m.metadata?.finishedAt ?? Date.now()}))
            )
        );
    }, [activeChatId, sending]);

    const refreshChats = useCallback(() => {
        if (!credentials) return;
        listChats(credentials)
            .then(setChats)
            .catch(() => {});
    }, [credentials]);

    const handleNewChat = useCallback(() => {
        setActiveChatId(null);
        setMessages([]);
        setInput('');
    }, []);

    const handleSelectChat = useCallback((chatId: string) => {
        setActiveChatId(chatId);
    }, []);

    const handleDeleteChat = useCallback(
        async (chatId: string) => {
            if (!credentials) return;
            await deleteChat(chatId, credentials);
            if (activeChatId === chatId) {
                setActiveChatId(null);
                setMessages([]);
            }
            refreshChats();
        },
        [credentials, activeChatId, refreshChats]
    );

    const handleSend = useCallback(async () => {
        const text = input.trim();
        if (!text || sending || !credentials) return;

        setInput('');
        setSending(true);
        setSuggestions([]);

        let chatId = activeChatId;
        if (!chatId) {
            const chat = await createChat(text.slice(0, 50), credentials);
            chatId = chat.id;
            setActiveChatId(chatId);
            refreshChats();
        }

        const userMsg: Message = {id: `msg-${Date.now()}`, role: 'user', text, timestamp: Date.now()};
        const assistantId = `msg-${Date.now()}-assistant`;
        setMessages((prev) => [...prev, userMsg, {id: assistantId, role: 'assistant', text: '', timestamp: Date.now()}]);

        const apiMessages: ApiMessage[] = [{id: userMsg.id, role: 'user', parts: [{type: 'text', text}]}];

        sendMessageStream(
            chatId,
            apiMessages,
            credentials,
            (delta) => {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? {...m, text: m.text + delta} : m)));
            },
            () => {
                setSending(false);
                refreshChats();
            },
            (err) => {
                setMessages((prev) => prev.map((m) => (m.id === assistantId ? {...m, text: `Error: ${err}`} : m)));
                setSending(false);
            },
            (event: StreamEvent) => {
                const addStep = (step: string) =>
                    setMessages((prev) => prev.map((m) => (m.id === assistantId ? {...m, steps: [...(m.steps ?? []), step]} : m)));
                switch (event.type) {
                    case 'reasoning-delta':
                        setMessages((prev) =>
                            prev.map((m) => {
                                if (m.id !== assistantId) return m;
                                const steps = [...(m.steps ?? [])];
                                const last = steps.length > 0 && steps[steps.length - 1].startsWith('💭 ') ? steps.pop()! : '💭 ';
                                return {...m, steps: [...steps, last + event.delta]};
                            })
                        );
                        break;
                    case 'tool-input-available':
                        addStep(`🔧 Calling **${event.toolName}**`);
                        break;
                    case 'tool-output-available':
                        // Mark the last tool step as complete and store output for the split panel
                        setMessages((prev) =>
                            prev.map((m) => {
                                if (m.id !== assistantId) return m;
                                const steps = [...(m.steps ?? [])];
                                const lastToolIdx = steps.map((s) => s.startsWith('🔧 ')).lastIndexOf(true);
                                if (lastToolIdx >= 0) {
                                    steps[lastToolIdx] = steps[lastToolIdx].replace('🔧 ', '✅ ');
                                }
                                return {...m, steps};
                            })
                        );
                        break;
                    case 'tool-output-error':
                        addStep(`❌ Tool error: ${event.errorText}`);
                        break;
                    case 'suggestions':
                        setSuggestions(event.suggestions);
                        break;
                }
            }
        );
    }, [input, sending, activeChatId, refreshChats, credentials]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <AppLayout
            navigationWidth={260}
            navigation={
                <ChatPanel
                    chats={chats}
                    activeChatId={activeChatId}
                    loading={chatsLoading}
                    onSelectChat={handleSelectChat}
                    onNewChat={handleNewChat}
                    onDeleteChat={handleDeleteChat}
                />
            }
            splitPanel={
                splitPanelOpen ? (
                    <SplitPanel header="Artifacts" closeBehavior="hide">
                        <Box padding="s">
                            {splitPanelContent ? (
                                <pre style={{whiteSpace: 'pre-wrap', fontSize: 13}}>{splitPanelContent}</pre>
                            ) : (
                                <Box color="text-body-secondary">Tool outputs and artifacts will appear here.</Box>
                            )}
                        </Box>
                    </SplitPanel>
                ) : undefined
            }
            splitPanelOpen={splitPanelOpen}
            onSplitPanelToggle={({detail}) => setSplitPanelOpen(detail.open)}
            splitPanelPreferences={{position: 'side'}}
            toolsHide
            content={
                <div className="cassandra-main">
                    <Header variant="h2" description="S&OP Forecasting Assistant">
                        Cassandra
                    </Header>
                    <div className="cassandra-messages" ref={scrollRef} role="log">
                        {messages.length === 0 && !sending && (
                            <div className="cassandra-empty-state">
                                <Avatar color="gen-ai" iconName="gen-ai" ariaLabel="Cassandra" width={48} />
                                <Box variant="h3">How can I help you today?</Box>
                                <Box color="text-body-secondary" textAlign="center">
                                    Ask about demand forecasts, S&OP data, or site volumes.
                                </Box>
                            </div>
                        )}
                        <div ref={contentRef} className="max-w-[900px] mx-auto px-4">
                            <SpaceBetween size="xs">
                                {messages.map((msg) => (
                                    <ChatMessage
                                        key={msg.id}
                                        role={msg.role}
                                        text={msg.text}
                                        loading={sending && msg.role === 'assistant' && !msg.text}
                                        steps={msg.steps}
                                        userAlias={userAlias}
                                        isStreaming={
                                            sending && msg.role === 'assistant' && msg.id === messages[messages.length - 1]?.id
                                        }
                                        timestamp={msg.timestamp}
                                    />
                                ))}
                            </SpaceBetween>
                            {!sending && suggestions.length > 0 && (
                                <div className="pl-8 pt-2">
                                    <SupportPromptGroup
                                        ariaLabel="Suggested follow-ups"
                                        alignment="horizontal"
                                        items={suggestions.map((s, i) => ({id: String(i), text: s}))}
                                        onItemClick={({detail}) => {
                                            const text = suggestions[+detail.id];
                                            if (text) {
                                                setInput(text);
                                            }
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        {!isAtBottom && (
                            <div style={{position: 'sticky', bottom: 32, float: 'right', marginRight: '5%'}}>
                                <Button
                                    variant="icon"
                                    iconName="angle-down"
                                    onClick={() => scrollToBottom()}
                                    ariaLabel="Scroll to bottom"
                                />
                            </div>
                        )}
                    </div>
                    <div className="cassandra-input-area max-w-[900px] mx-auto w-full px-4">
                        <div className="cassandra-prompt-container" onKeyDown={handleKeyDown}>
                            <Textarea
                                value={input}
                                onChange={({detail}) => setInput(detail.value)}
                                placeholder="Type your message here... (Enter to send, Shift+Enter for new line)"
                                disabled={sending}
                                rows={2}
                            />
                            <div className="cassandra-prompt-actions">
                                <Button
                                    variant="icon"
                                    iconName="view-vertical"
                                    ariaLabel="Toggle artifacts panel"
                                    onClick={() => setSplitPanelOpen(!splitPanelOpen)}
                                />
                                {sending ? (
                                    <Button variant="icon" iconName="close" ariaLabel="Stop" onClick={() => setSending(false)} />
                                ) : (
                                    <Button
                                        variant="primary"
                                        iconName="send"
                                        ariaLabel="Send message"
                                        onClick={handleSend}
                                        disabled={!input.trim()}
                                    />
                                )}
                            </div>
                        </div>
                        <div className="cassandra-disclaimer">Cassandra can make mistakes. Check important info.</div>
                    </div>
                </div>
            }
        />
    );
}
